import { useState } from 'preact/hooks';
import { CheckIcon, LockIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { AppShell } from '@/flows/marketplace/components/app-shell.js';
import { BackLink } from '@/flows/marketplace/components/back-link.js';
import { AddressFields } from '@/components/address-fields.js';
import { Alert } from '@/flows/marketplace/components/alert.js';
import { Button } from '@/components/button.js';
import { Card, CardIntro } from '@/flows/marketplace/components/card.js';
import { OrderSummary } from '@/flows/marketplace/components/order-summary.js';
import { Field } from '@/components/field.js';
import { PageHero } from '@/flows/marketplace/components/page-hero.js';
import { ProgressBar } from '@/flows/marketplace/components/progress-bar.js';
import { formatPhoneInput } from 'shared';
import { ApiError, getApiClient } from '@/lib/api-client.js';
import { buildCheckoutPayload } from '@/lib/build-checkout-payload.js';
import { checkoutErrorCopy, checkoutPayloadErrorCopy, type ErrorCopy } from '@/lib/error-copy.js';
import { cart, customer, quoteId, ratedAt, ratedProducts, vehicle } from '@/state/session.js';
import { cartFinanceValid } from '@/state/pricing.js';
import { step } from '@/flows/marketplace/state/step.js';
import { useRerate } from '@/flows/marketplace/use-rerate.js';
import {
  customerFieldError as err,
  customerForm,
  customerFormValidation,
  showCustomerErrors,
  updateCustomerForm as update,
} from '@/state/customer-form.js';

/**
 * Deliberately under the server's 30-minute `STALE_RATE_MS`
 * (`backend/src/modules/orders/validate-checkout.ts`) — the customer should be offered a
 * refresh before checkout starts rejecting the quote outright.
 */
const STALE_MINUTES = 25;
const STALE_THRESHOLD_MS = STALE_MINUTES * 60 * 1000;

/**
 * The last screen and the one that submits. Payment is settled per product before this
 * point, so the order summary above the form is the review — there is no separate one.
 */
export function CustomerInfo() {
  const form = customerForm.value;
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<ErrorCopy | null>(null);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  /** Set when checkout reports `STALE_RATE` before the local clock would have. */
  const [forcedStale, setForcedStale] = useState(false);
  const rerate = useRerate(() => setForcedStale(false));

  const ratedAtValue = ratedAt.value;
  const isStale =
    forcedStale || (ratedAtValue !== null && Date.now() - ratedAtValue > STALE_THRESHOLD_MS);

  async function handleSubmit(event: Event) {
    event.preventDefault();
    if (submitting) return;
    setAlert(null);

    const validated = customerFormValidation.value.customer;
    if (validated === null) {
      showCustomerErrors.value = true;
      return;
    }
    customer.value = validated;

    // A re-rate can move a price under a down payment that was valid when it was entered,
    // so this is re-checked at submit rather than trusted from the payment step.
    if (!cartFinanceValid.value) {
      setAlert({
        title: 'One of your plans needs another look',
        message: 'Prices changed since you set up financing. Edit the plan to continue.',
      });
      return;
    }

    if (vehicle.value === null || quoteId.value === null || cart.value.length === 0) {
      setAlert({
        title: 'Something is missing from your quote',
        message: 'Please go back and confirm your details before checkout.',
      });
      return;
    }

    const built = buildCheckoutPayload({
      quoteId: quoteId.value,
      customer: validated,
      vehicle: vehicle.value,
      cart: cart.value,
      ratedProducts: ratedProducts.value,
    });
    if (!built.ok) {
      setAlert(checkoutPayloadErrorCopy(built.reason));
      return;
    }

    setSubmitting(true);
    try {
      const res = await getApiClient().checkout(built.payload);
      // Leave `submitting` true so the button stays locked through the navigation.
      window.location.href = res.payment_link;
    } catch (error) {
      handleCheckoutError(error);
      setSubmitting(false);
    }
  }

  function handleCheckoutError(error: unknown) {
    if (error instanceof ApiError && error.code === 'STALE_RATE') {
      // Our window is the tighter one, so the clock check has normally flipped already and
      // this is unreachable. It stays for the skewed-clock case: a browser running slow
      // never flips on its own, and the generic copy ("reload the link") is a dead end
      // when the refresh button beside it is the actual fix.
      setForcedStale(true);
      return;
    }
    if (error instanceof ApiError && error.code === 'QUOTE_ALREADY_CONVERTED') {
      // The quote is already an order — show the terminal pane instead of leaving
      // the customer on a form whose submit would just re-hit this error.
      setAlreadyPaid(true);
      return;
    }
    setAlert(checkoutErrorCopy(error));
  }

  // The form lives in a signal, so stepping back and forward keeps what was typed.
  function handleBack() {
    step.value = 'tiers';
  }

  // `already_paid` is terminal (the quote is already an order), so it wins over the
  // recoverable stale/form panes.
  if (alreadyPaid) {
    return (
      <AppShell>
        <Card className="sm-card-centered">
          <div className="sm-center sm-badge" data-tone="success">
            <CheckIcon size={30} strokeWidth={3} aria-hidden="true" />
          </div>
          <CardIntro
            title="This checkout is already complete"
            subtitle="Look for an email from DocuSign in the next few minutes to sign your contract. Contact your dealer if it doesn't arrive."
          />
        </Card>
      </AppShell>
    );
  }

  if (isStale) {
    return (
      <AppShell>
        <BackLink onClick={handleBack} disabled={rerate.pending} />

        <PageHero
          title="Your quotes need a refresh"
          subtitle={`Prices update every ${STALE_MINUTES} minutes to stay accurate.`}
        />

        <ProgressBar />

        <Card>
          {rerate.error !== null && <Alert copy={rerate.error} />}
          <div className="sm-stack sm-cta-block">
            <Button onClick={rerate.run} loading={rerate.pending}>
              {rerate.pending ? 'Refreshing…' : 'Refresh quotes'}
            </Button>
          </div>
        </Card>

        <OrderSummary />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <BackLink onClick={handleBack} disabled={submitting} />

      <PageHero
        title="Review your order"
        subtitle="Check your plans and add your details. You'll pay securely on the next page."
      />

      <ProgressBar />

      <OrderSummary editable />

      <Card>
        {alert !== null && <Alert copy={alert} />}

        <form className="sm-stack sm-form" onSubmit={handleSubmit} noValidate>
          <div className="sm-stack sm-section">
            <p className="sm-type-eyebrow">Contact</p>

            <div className="sm-row-2">
              <Field
                id="sm-first-name"
                label="First name"
                labelAux="Required"
                placeholder="Jane"
                autoComplete="given-name"
                value={form.firstName}
                error={err('firstName')}
                onInput={(e) => update('firstName', (e.currentTarget as HTMLInputElement).value)}
              />
              <Field
                id="sm-last-name"
                label="Last name"
                labelAux="Required"
                placeholder="Doe"
                autoComplete="family-name"
                value={form.lastName}
                error={err('lastName')}
                onInput={(e) => update('lastName', (e.currentTarget as HTMLInputElement).value)}
              />
            </div>

            <Field
              id="sm-email"
              label="Email"
              labelAux="Required"
              type="email"
              placeholder="jane@example.com"
              autoComplete="email"
              inputMode="email"
              leadingIcon={MailIcon}
              value={form.email}
              error={err('email')}
              onInput={(e) => update('email', (e.currentTarget as HTMLInputElement).value)}
            />

            <Field
              id="sm-phone"
              label="Phone"
              labelAux="Required"
              type="tel"
              placeholder="(555) 123-4567"
              autoComplete="tel-national"
              inputMode="tel"
              leadingIcon={PhoneIcon}
              value={form.phone}
              error={err('phone')}
              onInput={(e) =>
                update('phone', formatPhoneInput((e.currentTarget as HTMLInputElement).value))
              }
            />
          </div>

          <div className="sm-stack sm-section">
            <p className="sm-type-eyebrow">Address</p>

            <AddressFields
              idPrefix="sm-customer"
              labelAux="Required"
              values={form}
              errors={{
                street1: err('street1'),
                city: err('city'),
                state: err('state'),
                postalCode: err('postalCode'),
              }}
              onChange={update}
            />
          </div>

          <div className="sm-stack sm-cta-block">
            <Button type="submit" loading={submitting}>
              {submitting ? 'Securely connecting…' : 'Go to checkout'}
            </Button>
            <p className="sm-type-caption sm-footnote">
              <LockIcon className="sm-footnote-icon" size={12} aria-hidden="true" />
              Payment processed securely. We never store your card.
            </p>
          </div>
        </form>
      </Card>
    </AppShell>
  );
}
